import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { ChevronDown, RefreshCw } from "@digital-twins/design-system/icons";
import {
  SocialUniverseItem,
  SocialUniverseRunItem,
} from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/Text";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { SocialReasonTooltip } from "./SocialReasonTooltip";
import {
  formatSocialCredits,
  runCreditLines,
  socialMonthLine,
  socialOfferCredits,
} from "./socialCredits";
import { useSocialListeningApi } from "./SocialListeningApiContext";
import { socialLimitErrorBody } from "./socialLimitErrors";
import { socialShortRunCopy } from "./socialTrackerStatus";
import { isChannelCreditPaused, socialUpdateNowState } from "./socialUpdateNow";
import { useNow } from "./useNow";
import { useRefreshCreditsOnEdge } from "./useRefreshCreditsOnEdge";
import {
  formatSocialCount,
  formatSocialDateTime,
  SOCIAL_PLATFORM_LABELS,
} from "./socialFormat";
import { isRunPhaseSorting } from "./socialTrackerStatus";
import { useSocialUniverseRuns } from "./useSocialUniverse";

interface SocialActivitySheetProps {
  universe: SocialUniverseItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
  /** True while a channel collects or sorts; the list polls meanwhile. */
  isBusy: boolean;
  /** A channel or "Update all" request is in flight. */
  isUpdating: boolean;
  onRetryChannel: (trackerToken: string) => void;
  onUpdateAll: () => void;
  /** Back office: each update also shows what the provider returned and billed. */
  showRunDiagnostics?: boolean;
  /** False where a run's offer cannot be accepted (the back office). Defaults to `canManage`. */
  canAcceptOffers?: boolean;
  /** A run's offer was accepted: its posts are now on the Topic. */
  onOfferAccepted?: () => void;
}

/** An offer that could not be accepted, shown on its run. */
interface SocialOfferError {
  runToken: string;
  message: string;
}

/** Where a run stands, as Activity words it; phases stay internal. */
export type SocialActivityRunState =
  | "collecting"
  | "sorting"
  | "failed"
  | "done";

export function socialActivityRunState(
  run: Pick<SocialUniverseRunItem, "status" | "phase" | "finishedAt">,
): SocialActivityRunState {
  if (run.status === "failed") {
    return "failed";
  }
  if (run.finishedAt == null) {
    if (run.status === "indexing" || isRunPhaseSorting(run.phase)) {
      return "sorting";
    }
    return "collecting";
  }
  return "done";
}

/**
 * Why a finished run kept less than it found, from its typed failure reason;
 * null when the reason has no user-facing line. A run stopped at the spend
 * guard (`budget`) reads "Nothing charged." from its credit line instead;
 * out of credits and maintenance replace the summary (`runCreditLines`).
 */
export function socialActivityRunReason(
  failureReason: SocialUniverseRunItem["failureReason"],
  t: TFunction<"app">,
): string | null {
  if (failureReason === "empty_after_filters") {
    return t("socialListening.topicActivity.reasonEmptyAfterFilters");
  }
  if (failureReason === "judge_unavailable") {
    return t("socialListening.topicActivity.reasonSortingStopped");
  }
  return null;
}

/** Activity lines of a finished run, split into the headline and the breakdown. */
interface SocialActivityRunCounts {
  /** Posts fetched and posts added: what every row shows. */
  headline: Array<string>;
  /** Why the rest were filtered out: not relevant, out of range and so on. */
  breakdown: Array<string>;
}

function runCounts(
  run: SocialUniverseRunItem,
  t: TFunction<"app">,
): SocialActivityRunCounts {
  const headline: Array<string> = [];
  const parts: Array<string> = [];
  // The billed number leads ("22 added"); what was found is a report, first in Details (S6).
  if (run.postsShown != null && run.postsFetched != null) {
    headline.push(
      t("socialListening.topicActivity.added", { count: run.postsShown }),
    );
    parts.push(
      t("socialListening.credits.run.found", { count: run.postsFetched }),
    );
    // Qualifies "added": why the page can list fewer than the run added.
    if ((run.postsHiddenOrRemoved ?? 0) > 0) {
      parts.push(
        t("socialListening.topicActivity.hiddenOrRemoved", {
          count: run.postsHiddenOrRemoved,
        }),
      );
    }
  } else if (run.postsFetched != null) {
    headline.push(
      t("socialListening.topicActivity.postsFetched", {
        count: run.postsFetched,
      }),
    );
  }
  if ((run.postsNotRelevant ?? 0) > 0) {
    parts.push(
      t("socialListening.topicActivity.notRelevant", {
        count: run.postsNotRelevant,
      }),
    );
  }
  if (run.itemsOutOfWindow > 0) {
    parts.push(
      run.depthDays != null
        ? t("socialListening.topicActivity.olderThanDays", {
            count: run.itemsOutOfWindow,
            days: run.depthDays,
          })
        : t("socialListening.detail.runOlderThanLastUpdate", {
            count: run.itemsOutOfWindow,
          }),
    );
  }
  if ((run.itemsUnjudged ?? 0) > 0) {
    parts.push(
      t("socialListening.topicActivity.waitingToSort", {
        count: run.itemsUnjudged,
      }),
    );
  }
  if ((run.postsOverLimit ?? 0) > 0) {
    parts.push(
      t("socialListening.topicActivity.overLimit", {
        count: run.postsOverLimit,
      }),
    );
  }
  const other = socialActivityOtherPosts(run);
  if (other > 0) {
    parts.push(t("socialListening.topicActivity.other", { count: other }));
  }
  // Without a fetched count there is no headline: the reasons lead instead.
  if (headline.length === 0) {
    return { headline: parts, breakdown: [] };
  }
  return { headline, breakdown: parts };
}

/**
 * Posts fetched that no Activity line accounts for, so the lines always add
 * up to the posts fetched (for example posts already collected by an earlier
 * update). 0 when the run has no fetched count or shown count. Hidden or
 * removed posts are part of added, so they never count here.
 */
export function socialActivityOtherPosts(
  run: Pick<
    SocialUniverseRunItem,
    | "postsFetched"
    | "postsShown"
    | "postsNotRelevant"
    | "itemsOutOfWindow"
    | "itemsUnjudged"
    | "postsOverLimit"
    | "heldOffer"
  >,
): number {
  if (run.postsFetched == null || run.postsShown == null) {
    return 0;
  }
  const listed =
    run.postsShown +
    (run.postsNotRelevant ?? 0) +
    run.itemsOutOfWindow +
    (run.itemsUnjudged ?? 0) +
    (run.postsOverLimit ?? 0) +
    (run.heldOffer?.posts ?? 0);
  return Math.max(0, run.postsFetched - listed);
}

/** Time an Activity row shows: when the run finished, else when it started. */
function runTime(run: SocialUniverseRunItem): number {
  const time = Date.parse(run.finishedAt ?? run.startedAt ?? "");
  // A run with no time yet has only just been queued: it is the newest.
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}

/** Activity rows newest first, by the time each row shows. */
export function sortSocialActivityRuns(
  runs: Array<SocialUniverseRunItem>,
): Array<SocialUniverseRunItem> {
  return [...runs].sort((a, b) => runTime(b) - runTime(a));
}

/** "Tequila · Activity": every update across the Topic's channels, newest first. */
export function SocialActivitySheet(props: SocialActivitySheetProps) {
  const {
    universe,
    open,
    onOpenChange,
    canManage,
    isBusy,
    isUpdating,
    onRetryChannel,
    onUpdateAll,
    showRunDiagnostics = false,
    canAcceptOffers = canManage,
    onOfferAccepted,
  } = props;

  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(
    () => new Set(),
  );
  const [acceptingOffer, setAcceptingOffer] = useState<string | null>(null);
  const [offerError, setOfferError] = useState<SocialOfferError | null>(null);

  const { t, i18n } = useTranslation("app");
  const socialListeningApi = useSocialListeningApi();
  const {
    runs: loadedRuns,
    isLoading,
    hasError,
    refresh: refreshRuns,
  } = useSocialUniverseRuns(open && universe ? universe.token : null, isBusy);
  const runs = sortSocialActivityRuns(loadedRuns);
  const refreshCredits = useRefreshCreditsOnEdge();
  const now = useNow(universe?.manualUpdateAvailableAt);

  // Settlement can trail the last poll (judging tail, hourly sweep): refresh on open (E1).
  useEffect(() => {
    if (open) {
      refreshCredits();
    }
  }, [open, refreshCredits]);

  const latestRunByChannel = new Map<string, string>();
  for (const run of runs) {
    if (!latestRunByChannel.has(run.trackerToken)) {
      latestRunByChannel.set(run.trackerToken, run.token);
    }
  }
  const isEveryChannelPaused =
    universe != null &&
    universe.channels.length > 0 &&
    universe.channels.every((channel) => channel.status === "paused");

  const updateAll = socialUpdateNowState({
    t,
    lng: i18n.language,
    now,
    isCreditPaused: universe?.channels.some(isChannelCreditPaused) ?? false,
    availableAt: universe?.manualUpdateAvailableAt,
    maxCreditsPerUpdate: universe?.maxCreditsPerUpdate,
    costKey: "updateAll",
  });
  const isUpdateAllDisabled = isUpdating || isEveryChannelPaused || updateAll.isBlocked;
  // Only when someone else pays: "Paid from Ana's credits." (pool orgs show nothing).
  const payerName =
    universe?.payer?.kind === "seat" &&
    !universe.payer.isViewer &&
    universe.payer.displayName
      ? universe.payer.displayName
      : null;

  function toggleRunBreakdown(runToken: string) {
    setExpandedRuns((current) => {
      const next = new Set(current);
      if (next.has(runToken)) {
        next.delete(runToken);
      } else {
        next.add(runToken);
      }
      return next;
    });
  }

  async function acceptOffer(runToken: string) {
    if (!universe) {
      return;
    }
    setAcceptingOffer(runToken);
    setOfferError(null);
    try {
      await socialListeningApi.unlockRun(universe.token, runToken);
    } catch (error: unknown) {
      const isNoFunds = socialLimitErrorBody(error)?.code === "social_out_of_credits";
      setOfferError({
        runToken,
        message: isNoFunds
          ? t("socialListening.topicActivity.offerNoCredits")
          : t("socialListening.topicToast.actionFailed"),
      });
      setAcceptingOffer(null);
      return;
    }
    refreshCredits();
    onOfferAccepted?.();
    // The posts are in: a failed reload leaves the old offer, and accepting again charges nothing.
    await Promise.resolve(refreshRuns()).catch(() => undefined);
    setAcceptingOffer(null);
  }

  function renderRun(run: SocialUniverseRunItem, index: number) {
    const state = socialActivityRunState(run);
    const time = formatSocialDateTime(
      run.finishedAt ?? run.startedAt,
      i18n.language,
    );
    const credit = runCreditLines(run, {
      t,
      lng: i18n.language,
    });
    // A failed run can still have added posts: they count on the page and in the month line.
    const isFailedWithPosts =
      state === "failed" && run.postsFetched != null && (run.postsShown ?? 0) > 0;
    const doneCounts =
      state === "done" || isFailedWithPosts
        ? runCounts(run, t)
        : { headline: [], breakdown: [] };
    // "22 added · 3.7 credits"; the credit details close the breakdown (E4).
    const counts = {
      headline: credit.headlineSuffix
        ? [
            ...(doneCounts.headline.length > 0
              ? doneCounts.headline
              : [t("socialListening.topicActivity.noNewPosts")]),
            credit.headlineSuffix,
          ]
        : doneCounts.headline,
      breakdown: [...doneCounts.breakdown, ...credit.details],
    };
    const hasBreakdown = counts.breakdown.length > 0;
    const isExpanded = hasBreakdown && expandedRuns.has(run.token);
    const breakdownId = `social-activity-run-breakdown-${index}-list`;
    const isLatest = latestRunByChannel.get(run.trackerToken) === run.token;
    const description = credit.summaryOverride
      ? credit.summaryOverride
      : state === "failed" && !isFailedWithPosts
        ? t("socialListening.topicActivity.couldNotUpdate")
        : state === "collecting"
          ? t("socialListening.topicActivity.collecting")
          : state === "sorting"
            ? t("socialListening.topicActivity.sorting")
            : counts.headline.length > 0
              ? counts.headline.join(" · ")
              : t("socialListening.topicActivity.noNewPosts");
    const reason = isFailedWithPosts
      ? t("socialListening.topicActivity.couldNotUpdate")
      : state === "done"
        ? socialActivityRunReason(run.failureReason, t)
        : null;
    const shortRun = state === "done" ? socialShortRunCopy(run) : null;
    const offerCredits = run.heldOffer ? socialOfferCredits(run.heldOffer.credits) : 0;

    return (
      <li
        key={run.token}
        className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 py-3 text-[13px] sm:grid-cols-[minmax(0,140px)_minmax(0,110px)_1fr_auto]"
        data-testid={`social-activity-run-${run.token}`}
        data-state={state}
      >
        <span className="text-muted-foreground">{time ?? ""}</span>
        <span className="inline-flex items-center gap-1.5 text-foreground">
          <SocialPlatformIcon platform={run.platform} className="h-3.5 w-3.5" />
          {SOCIAL_PLATFORM_LABELS[run.platform]}
        </span>
        <div className="col-span-2 min-w-0 text-foreground sm:col-span-1">
          <span data-testid={`social-activity-run-${run.token}-summary`}>
            {description}
          </span>
          {reason && !credit.summaryOverride ? (
            <Text
              as="span"
              size="xs"
              color="muted"
              className="block"
              data-testid={`social-activity-run-${run.token}-reason`}
            >
              {reason}
            </Text>
          ) : null}
          {shortRun ? (
            <Text
              as="span"
              size="xs"
              color="muted"
              className="block"
              data-testid={`social-activity-run-${run.token}-short`}
            >
              {t(shortRun.key, shortRun.values)}
            </Text>
          ) : null}
          {credit.statusLine ? (
            <Text
              as="span"
              size="xs"
              color="muted"
              className="block tabular-nums"
              data-testid={`social-activity-run-${run.token}-credits`}
            >
              {credit.statusLine}
            </Text>
          ) : null}
          {hasBreakdown ? (
            <button
              type="button"
              className="group ml-2 inline-flex cursor-pointer items-center gap-0.5 rounded-md align-baseline transition-colors"
              aria-expanded={isExpanded}
              aria-controls={breakdownId}
              data-testid={`social-activity-run-breakdown-${index}`}
              onClick={() => toggleRunBreakdown(run.token)}
            >
              <Text
                as="span"
                size="xs"
                color="muted"
                className="transition-colors group-hover:text-foreground"
              >
                {t("socialListening.topicActivity.details")}
              </Text>
              <ChevronDown
                className={cn(
                  "h-3 w-3 shrink-0 text-muted-foreground transition-[transform,color] group-hover:text-foreground",
                  isExpanded ? "rotate-180" : "",
                )}
                aria-hidden="true"
              />
            </button>
          ) : null}
          {isExpanded ? (
            <Text
              as="span"
              size="xs"
              color="muted"
              className="mt-1 block"
              id={breakdownId}
              data-testid={breakdownId}
            >
              {counts.breakdown.join(" · ")}
            </Text>
          ) : null}
          {run.heldOffer && canAcceptOffers ? (
            <div
              className="mt-1 flex flex-wrap items-center gap-2"
              data-testid={`social-activity-run-${run.token}-offer`}
            >
              <Text as="span" size="xs" color="muted">
                {t("socialListening.topicActivity.offer", {
                  count: run.heldOffer.posts,
                })}
              </Text>
              <Button
                type="button"
                variant="outline"
                size="sm"
                shape="pill"
                className="h-7 px-3 text-xs"
                disabled={acceptingOffer === run.token}
                data-testid={`social-activity-run-${run.token}-offer-accept`}
                onClick={() => void acceptOffer(run.token)}
              >
                {t("socialListening.topicActivity.offerAccept", {
                  count: offerCredits,
                  value: formatSocialCredits(offerCredits, i18n.language),
                })}
              </Button>
              {offerError?.runToken === run.token ? (
                <Text
                  as="span"
                  size="xs"
                  color="muted"
                  className="w-full"
                  role="status"
                  data-testid={`social-activity-run-${run.token}-offer-error`}
                >
                  {offerError.message}
                </Text>
              ) : null}
            </div>
          ) : null}
          {showRunDiagnostics ? (
            <Text
              as="span"
              size="xs"
              color="muted"
              className="block"
              data-testid={`social-activity-run-${run.token}-diagnostics`}
            >
              {[
                t("socialListening.detail.runFetched", {
                  value: formatSocialCount(run.itemsFetched, i18n.language),
                }),
                ...(run.postLimit != null
                  ? [
                      t("socialListening.detail.runPostLimit", {
                        value: run.postLimit,
                      }),
                    ]
                  : []),
                ...(run.costUsd != null
                  ? [
                      t("socialListening.detail.runCost", {
                        value: `$${run.costUsd.toFixed(3)}`,
                      }),
                    ]
                  : []),
                ...(run.phase ? [run.phase] : []),
                // Back office only: which term set the charge.
                ...(run.chargeBasis ? [run.chargeBasis] : []),
                // Raw server reasons are English and name internals: diagnostics only.
                ...(run.error ? [run.error] : []),
              ].join(" · ")}
            </Text>
          ) : null}
        </div>
        {state === "failed" && isLatest && canManage ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            shape="pill"
            className="col-span-2 h-8 justify-self-start px-3 text-[13px] sm:col-span-1"
            disabled={isUpdating}
            data-testid={`social-activity-retry-${run.token}`}
            onClick={() => onRetryChannel(run.trackerToken)}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t("socialListening.topicActivity.retry")}
          </Button>
        ) : (
          <span className="hidden sm:block" />
        )}
      </li>
    );
  }

  const body = isLoading ? (
    <div className="space-y-2">
      <Skeleton className="h-10 rounded-xl" />
      <Skeleton className="h-10 rounded-xl" />
    </div>
  ) : hasError ? (
    <Text size="sm" color="muted" data-testid="social-activity-error">
      {t("socialListening.topicActivity.loadFailed")}
    </Text>
  ) : runs.length === 0 ? (
    <Text size="sm" color="muted" data-testid="social-activity-empty">
      {t("socialListening.topicActivity.empty")}
    </Text>
  ) : (
    <ul className="divide-y divide-border" data-testid="social-activity-list">
      {runs.map((run, index) => renderRun(run, index))}
    </ul>
  );

  return (
    <Sheet open={open && universe != null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-[720px]"
        data-testid="social-activity-sheet"
      >
        {universe ? (
          <>
            <SheetHeader className="border-b border-border p-6 text-left">
              <SheetTitle>
                {t("socialListening.topicActivity.title", {
                  name: universe.name,
                })}
              </SheetTitle>
              <SheetDescription data-testid="social-activity-month">
                {socialMonthLine(
                  t,
                  i18n.language,
                  universe.creditsThisMonth,
                  universe.postsKeptThisMonth,
                )}
                {payerName ? (
                  <span className="block" data-testid="social-activity-payer">
                    {t("socialListening.credits.payer", { name: payerName })}
                  </span>
                ) : null}
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 p-6">{body}</div>
            {canManage ? (
              <div className="flex justify-end border-t border-border p-6">
                <SocialReasonTooltip
                  reason={updateAll.reason}
                  isDisabled={isUpdateAllDisabled}
                  testId="social-activity-update-all"
                >
                  <Button
                    type="button"
                    variant="cta"
                    shape="pill"
                    className="h-9 px-4"
                    disabled={isUpdateAllDisabled}
                    data-testid="social-activity-update-all"
                    onClick={onUpdateAll}
                  >
                    <RefreshCw
                      className="mr-1.5 h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                    {t("socialListening.topicActivity.updateAll")}
                  </Button>
                </SocialReasonTooltip>
              </div>
            ) : null}
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
